import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Adresse française avec autocomplete CP/Ville via l'API officielle geo.api.gouv.fr.
 *
 * Comportement :
 *  - Si l'utilisateur saisit un CP (ex: 31000, 31100, 31400), la ville est proposée automatiquement.
 *  - Si l'utilisateur saisit une ville (ex: "Toulouse"), les CPs correspondants sont proposés.
 *  - Une saisie partielle d'un CP (ex: "310") accepte n'importe quelle ville commençant par les 2 premiers chiffres (département).
 *
 * Props :
 *  - value : { street, postal_code, city }
 *  - onChange(next) : reçoit le même objet à jour
 *  - streetRequired : bool (défaut true)
 */
export default function AddressFields({ value = {}, onChange, onSelect, streetRequired = true }) {
  const { street = "", postal_code = "", city = "" } = value;
  const [suggestions, setSuggestions] = useState([]);
  const [activeField, setActiveField] = useState(null); // 'cp' | 'city' | null
  const debounceRef = useRef(null);

  const set = (patch) => onChange({ ...value, ...patch });

  // Debounced fetch on CP / city change
  useEffect(() => {
    if (!activeField) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const flat = [];
        if (activeField === "cp") {
          // 5 digits: exact codePostal query
          if (/^\d{5}$/.test(postal_code)) {
            const res = await fetch(`https://geo.api.gouv.fr/communes?codePostal=${postal_code}&fields=nom,codesPostaux,centre,code,population,centre&limit=20`);
            const data = await res.json();
            for (const c of data || []) {
              for (const cp of c.codesPostaux || []) {
                if (cp === postal_code) flat.push({ city: c.nom, cp, code: c.code, centre: c.centre });
              }
            }
          }
          // 2-4 digits: use departement endpoint (first 2 digits = dept code)
          if (postal_code.length >= 2 && postal_code.length <= 4) {
            const dept = postal_code.slice(0, 2);
            const r2 = await fetch(`https://geo.api.gouv.fr/departements/${dept}/communes?fields=nom,codesPostaux,centre&limit=100`);
            const d2 = await r2.json();
            for (const c of d2 || []) {
              for (const cp of c.codesPostaux || []) {
                if (cp.startsWith(postal_code)) flat.push({ city: c.nom, cp, code: c.code, centre: c.centre });
              }
            }
          }
        } else if (activeField === "city" && city.length >= 2) {
          const res = await fetch(`https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(city)}&fields=nom,codesPostaux,centre,code,population,centre&boost=population&limit=20`);
          const data = await res.json();
          for (const c of data || []) {
            for (const cp of c.codesPostaux || []) {
              flat.push({ city: c.nom, cp, code: c.code, centre: c.centre });
            }
          }
        }
        // De-duplicate by cp+city
        const seen = new Set();
        const unique = flat.filter((s) => {
          const k = `${s.cp}|${s.city}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        setSuggestions(unique.slice(0, 30));
      } catch {
        setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postal_code, city, activeField]);

  const pick = (s) => {
    set({ postal_code: s.cp, city: s.city });
    setSuggestions([]);
    setActiveField(null);
    // Notify parent that an address was selected so the map can zoom on it
    onSelect && onSelect(s);
  };

  return (
    <div className="space-y-3" data-testid="address-fields">
      <div>
        <Label>Rue {streetRequired && <span className="text-rose-500">*</span>}</Label>
        <Input
          value={street}
          onChange={(e) => set({ street: e.target.value })}
          placeholder="10 rue des Lilas"
          className="rounded-2xl mt-1 py-6"
          required={streetRequired}
          data-testid="address-street"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3 relative">
        <div>
          <Label>Code postal <span className="text-xs text-stone-400 font-normal">(facultatif)</span></Label>
          <Input
            value={postal_code}
            onChange={(e) => { set({ postal_code: e.target.value.replace(/\D/g, "").slice(0, 5) }); setActiveField("cp"); }}
            onFocus={() => setActiveField("cp")}
            onBlur={() => setTimeout(() => setActiveField(null), 200)}
            placeholder="31000"
            className="rounded-2xl mt-1 py-6"
            inputMode="numeric"
            data-testid="address-cp"
          />
        </div>
        <div>
          <Label>Ville <span className="text-rose-500">*</span></Label>
          <Input
            value={city}
            onChange={(e) => { set({ city: e.target.value }); setActiveField("city"); }}
            onFocus={() => setActiveField("city")}
            onBlur={() => setTimeout(() => setActiveField(null), 200)}
            placeholder="Toulouse"
            className="rounded-2xl mt-1 py-6"
            required
            data-testid="address-city"
          />
        </div>
        {/* Suggestions dropdown */}
        {activeField && suggestions.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-2xl border-2 border-orange-200 shadow-2xl max-h-64 overflow-y-auto" style={{ zIndex: 1000 }} data-testid="address-suggestions">
            {suggestions.map((s, i) => (
              <button
                type="button"
                key={`${s.cp}-${s.code}-${i}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(s)}
                className="w-full text-left px-4 py-2.5 hover:bg-rose-50 flex items-center gap-2 text-sm border-b border-stone-100 last:border-0"
                data-testid={`address-suggestion-${i}`}
              >
                <MapPin size={14} weight="duotone" className="text-orange-500 flex-shrink-0" />
                <span className="font-bold text-stone-700">{s.cp}</span>
                <span className="text-stone-500">·</span>
                <span className="text-stone-700">{s.city}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
